import { __decorate, __metadata } from '../../node_modules/tslib/tslib.es6.js';
import '../../node_modules/lit/node_modules/@lit/reactive-element/reactive-element.js';
import { html as x, nothing as A } from '../../node_modules/lit-html/lit-html.js';
import '../../node_modules/lit-element/lit-element.js';
import { property as n } from '../../node_modules/lit/node_modules/@lit/reactive-element/decorators/property.js';
import { state as t } from '../../node_modules/lit/node_modules/@lit/reactive-element/decorators/state.js';
import '../../node_modules/lit/node_modules/@lit/reactive-element/decorators/query-assigned-elements.js';
import { ifDefined as l } from '../../node_modules/lit-html/directives/if-defined.js';
import getClassName from '../../node_modules/@fmr-ap142296/brick-helpers/dist/src/functions/get-class-name.js';
import { ScopedElementsMixin } from '../utilities/scoped-elements/scoped-elements.mixin.js';
import { PvdSlotifyBaseComponent } from '../pvd-base-component/pvd-slotify-base-component.js';
import { PvdAnimation } from '../pvd-utils/scripts/animation.js';
import { StandardSize, Size } from '../pvd-utils/enums/size.js';

var ExpandCollapseState;
(function (ExpandCollapseState) {
    ExpandCollapseState["collapsed"] = "collapsed";
    ExpandCollapseState["collapsing"] = "collapsing";
    ExpandCollapseState["expanded"] = "expanded";
    ExpandCollapseState["expanding"] = "expanding";
})(ExpandCollapseState || (ExpandCollapseState = {}));
const EXPAND_COLLAPSE_ANIMATION_DURATION = 250;
const DEFAULT_DISABLE_ANIMATION = false;
const DEFAULT_EXPAND = false;
const DEFAULT_EXPAND_ON_PRINT = false;
const DEFAULT_SIZE = StandardSize.medium;
/**
 * Providence Expand-Collapse component.
 * @see {@link https://providence.fmr.com/4.x/components/expand-collapse/expand-collapse.html|Expand-Collapse documentation}
 */
class PvdExpandCollapse extends ScopedElementsMixin(PvdSlotifyBaseComponent) {
    constructor() {
        super(...arguments);
        //#endregion
        // #region LitElement properties
        // public event handlers
        this.animationEndEventHandler = () => {
            this.pvdExpand ? this.expanded() : this.collapsed();
            this.removeAnimationEndEvent();
        };
        this.afterPrintEventHandler = () => {
            if (!this._expandStateBeforePrint) {
                this.collapsed();
            }
        };
        this.beforePrintEventHandler = () => {
            this._expandStateBeforePrint = this.pvdExpand;
            this.expanded();
        };
        this.pvdDisableAnimation = DEFAULT_DISABLE_ANIMATION;
        /**
         * We have to make this a regular getter/setter property instead of a reactive
         * property because a reactive property will cause a re-render on change. If
         * the component is re-rendered, the expand/collapse animation will not work.
         */
        this._pvdExpand = DEFAULT_EXPAND;
        this.pvdExpandOnPrint = DEFAULT_EXPAND_ON_PRINT;
        this.pvdSize = DEFAULT_SIZE;
        // #endregion
    }
    get pvdExpand() {
        return this._pvdExpand;
    }
    set pvdExpand(newVal) {
        this._pvdExpand = newVal;
        if (!this.pvdExpand) {
            this.removeAttribute('pvd-expand');
        }
        else {
            this.setAttribute('pvd-expand', '');
        }
        this.expandStateChange();
    }
    static get scopedElements() {
        // $namespace-$name-$extendingComponentName
        return {
            'pvd-scoped-icon': customElements.get(`${this.namespace}-icon`),
        };
    }
    // #endregion
    // #region public methods
    toggleExpandState() {
        this.pvdExpand = !this.pvdExpand;
        this.expandStateChange();
    }
    addAnimationEndEvent() {
        this._contentEl.addEventListener('pvd-animation:end', this.animationEndEventHandler);
    }
    removeAnimationEndEvent() {
        this._contentEl.removeEventListener('pvd-animation:end', this.animationEndEventHandler);
    }
    addExpandOnPrintEventListener() {
        window.addEventListener('beforeprint', this.beforePrintEventHandler);
        window.addEventListener('afterprint', this.afterPrintEventHandler);
    }
    removeExpandOnPrintEventListener() {
        window.removeEventListener('beforeprint', this.beforePrintEventHandler);
        window.removeEventListener('afterprint', this.afterPrintEventHandler);
    }
    // #endregion
    // #region protected methods
    get hasSubHeading() {
        return this.isSlotUsed('subheading');
    }
    get iconSize() {
        return this.pvdSize === StandardSize.large ? Size.medium : Size.small;
    }
    get prefersReducedMotionSetting() {
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        return prefersReducedMotion.matches;
    }
    // #endregion
    // # region private methods
    expandStateChange() {
        if (this.pvdExpand && this._state === ExpandCollapseState.collapsed) {
            this.expanding();
        }
        else if (!this.pvdExpand && this._state === ExpandCollapseState.expanded) {
            this.collapsing();
        }
    }
    async expanding() {
        this._state = ExpandCollapseState.expanding;
        // waiting for modifier classes to update
        await this.requestUpdate();
        this.triggerChangeEvent();
        // if `prefers-reduced-motion: reduce` or pvdDisableAnimation are enabled, bypass animation
        if (this.prefersReducedMotionSetting || this.pvdDisableAnimation) {
            this.expanded();
        }
        else {
            this.animateContent();
        }
    }
    expanded() {
        this._state = ExpandCollapseState.expanded;
        // needing to set height from animation to `auto` for dynamic content
        this._contentEl.style.height = 'auto';
        this.triggerChangeEvent();
        this.triggerExpandedEvent();
    }
    async collapsing() {
        this._state = ExpandCollapseState.collapsing;
        // waiting for modifier classes to update
        await this.requestUpdate();
        this.triggerChangeEvent();
        // if `prefers-reduced-motion: reduce` or pvdDisableAnimation are enabled, bypass animation
        if (this.prefersReducedMotionSetting || this.pvdDisableAnimation) {
            this.collapsed();
        }
        else {
            this.animateContent();
        }
    }
    collapsed() {
        this._state = ExpandCollapseState.collapsed;
        this.triggerChangeEvent();
        this.triggerCollapsedEvent();
    }
    animateContent() {
        this._contentHeight = this._contentEl.scrollHeight;
        this._destHeight = this._state === ExpandCollapseState.expanding ? this._contentHeight : 0;
        const animation = new PvdAnimation(this._contentEl, 'height', EXPAND_COLLAPSE_ANIMATION_DURATION);
        setTimeout(() => {
            this.addAnimationEndEvent();
            animation.start(this._destHeight);
        }, 0);
    }
    triggerChangeEvent() {
        const event = new CustomEvent('pvd-expand-collapse-change', {
            detail: { expand: this.pvdExpand, state: this._state },
        });
        this.dispatchEvent(event);
    }
    triggerExpandedEvent() {
        const event = new CustomEvent('pvd-expand-collapse-expanded', {});
        this.dispatchEvent(event);
    }
    triggerCollapsedEvent() {
        const event = new CustomEvent('pvd-expand-collapse-collapsed', {});
        this.dispatchEvent(event);
    }
    /**
     * Creates a generated id for the interaction button
     * @return a generated id to bee used for the interaction button
     */
    get generatedInteractionButtonId() {
        if (!this._generatedInteractionButtonId) {
            this._generatedInteractionButtonId = `pvd-expand-collapse-interaction-button-id-${PvdSlotifyBaseComponent.generateRandomNumber()}`;
        }
        return this._generatedInteractionButtonId;
    }
    /**
     * Creates a generated id for the content
     * @return a generated id to be used for the content wrapper
     */
    get generatedContentId() {
        if (!this._generatedContentId) {
            this._generatedContentId = `pvd-expand-collapse-content-id-${PvdSlotifyBaseComponent.generateRandomNumber()}`;
        }
        return this._generatedContentId;
    }
    /**
     * Creates a generated id for the subheading
     * @return a generated id to be used for the subheading wrapper
     */
    get generatedSubheadingId() {
        if (!this._generatedSubheadingId) {
            this._generatedSubheadingId = `pvd-expand-collapse-subheading-id-${PvdSlotifyBaseComponent.generateRandomNumber()}`;
        }
        return this._generatedSubheadingId;
    }
    // #endregion
    // #region LitElement lifecycles
    updated(changedProperties) {
        super.updated(changedProperties);
        if (changedProperties.has('pvdExpand')) {
            this.expandStateChange();
        }
        if (changedProperties.has('pvdExpandOnPrint')) {
            this.pvdExpandOnPrint
                ? this.addExpandOnPrintEventListener()
                : this.removeExpandOnPrintEventListener();
        }
    }
    firstUpdated() {
        super.firstUpdated();
        this._pvdExpand =
            this.hasAttribute('pvd-expand') && this.getAttribute('pvd-expand') !== 'false';
        this._contentEl = this.querySelector('.pvd-expand-collapse__content-wrapper');
        this._state = this.pvdExpand ? ExpandCollapseState.expanded : ExpandCollapseState.collapsed;
    }
    static get observedAttributes() {
        return this._observedAttributes;
    }
    static set observedAttributes(value) {
        this._observedAttributes = value;
    }
    // we manually handle pvd-expand to allow for animations
    attributeChangedCallback(name, _old, value) {
        if (name === 'pvd-expand' && _old !== value) {
            this.pvdExpand = value !== null && value !== 'false';
        }
    }
    //#endregion
    /**
     * Determine if PvdTitleHeadingLevel value is set and if the value is allowed.
     * Allowed heading levels are 2, 3, 4, 5, or 6 of type Number.
     * @returns boolean true if PvdTitleHeadingLevel value is set and allowed
     */
    get hasTitleHeadingSet() {
        const allowedHeadingLevels = [2, 3, 4, 5, 6];
        return this.pvdTitleHeadingLevel && allowedHeadingLevels.includes(this.pvdTitleHeadingLevel);
    }
    renderButton() {
        return x `<button @click="${this.toggleExpandState}" aria-expanded="${this.pvdExpand}" aria-controls="${this.generatedContentId}" aria-describedby="${l(this.hasSubHeading ? this.generatedSubheadingId : undefined)}" class="pvd-expand-collapse__interactive-button" id="${this.generatedInteractionButtonId}" type="button">${this.renderIcon()} ${this.renderSlottedHeading()}</button>`;
    }
    renderIcon() {
        return this.scopeTemplate `
      <pvd-scoped-icon
        pvd-icon-class="pvd-expand-collapse__icon"
        pvd-name="action__caret-right"
        pvd-size="${this.iconSize}"
      >
      </pvd-scoped-icon>
    `;
    }
    renderHeading() {
        switch (this.pvdTitleHeadingLevel) {
            case 2:
                return x `<h2 class="pvd-expand-collapse__heading">${this.renderButton()}</h2>`;
            case 3:
                return x `<h3 class="pvd-expand-collapse__heading">${this.renderButton()}</h3>`;
            case 4:
                return x `<h4 class="pvd-expand-collapse__heading">${this.renderButton()}</h4>`;
            case 5:
                return x `<h5 class="pvd-expand-collapse__heading">${this.renderButton()}</h5>`;
            case 6:
                return x `<h6 class="pvd-expand-collapse__heading">${this.renderButton()}</h6>`;
        }
    }
    renderSlottedHeading() {
        return x `<div class="pvd-expand-collapse__heading-wrapper"><s-slot name="heading"></s-slot></div>`;
    }
    renderSubheading() {
        return x `<div id="${this.generatedSubheadingId}" class="pvd-expand-collapse__subheading-wrapper"><s-slot name="subheading"></s-slot></div>`;
    }
    render() {
        const classMap = {
            default: `${this.constructor.namespace}-expand-collapse-root`,
            modifierPrefix: 'pvd-expand-collapse',
            modifiers: [
                {
                    condition: true,
                    className: `${this.pvdSize}`,
                },
                {
                    condition: true,
                    className: this._state,
                },
            ],
        };
        return x `<div class="${getClassName(classMap)}">${this.hasTitleHeadingSet ? this.renderHeading() : this.renderButton()} ${this.hasSubHeading ? this.renderSubheading() : A}<div aria-hidden="${!this.pvdExpand}" aria-labelledby="${this.generatedInteractionButtonId}" class="pvd-expand-collapse__content-wrapper" id="${this.generatedContentId}" role="region"><s-slot></s-slot></div></div>`;
    }
}
//#region Component configuration
PvdExpandCollapse.componentName = 'expand-collapse';
//#region attribute change functionality
// we need to override to be able to watch pvd-expand for animations
PvdExpandCollapse._observedAttributes = [
    'pvd-expand',
    'pvd-expand-on-print',
    'pvd-disable-animation',
    'pvd-size',
    'pvd-title-heading-level',
];
__decorate([
    t(),
    __metadata("design:type", String)
], PvdExpandCollapse.prototype, "_state", void 0);
__decorate([
    n({
        type: Boolean,
        reflect: true,
        attribute: 'pvd-disable-animation',
        ...PvdSlotifyBaseComponent.booleanAttribute('pvd-disable-animation'),
    }),
    __metadata("design:type", Boolean)
], PvdExpandCollapse.prototype, "pvdDisableAnimation", void 0);
__decorate([
    n({
        type: Boolean,
        reflect: true,
        attribute: 'pvd-expand-on-print',
        ...PvdSlotifyBaseComponent.booleanAttribute('pvd-expand-on-print'),
    }),
    __metadata("design:type", Boolean)
], PvdExpandCollapse.prototype, "pvdExpandOnPrint", void 0);
__decorate([
    n({
        type: String,
        reflect: true,
        attribute: 'pvd-size',
        ...PvdSlotifyBaseComponent.hasChanged(StandardSize),
    }),
    __metadata("design:type", Number)
], PvdExpandCollapse.prototype, "pvdSize", void 0);
__decorate([
    n({
        type: Number,
        reflect: true,
        attribute: 'pvd-title-heading-level',
    })
    // no default because this is an opt-in property; providing a default could break consumers slotting in a heading
    ,
    __metadata("design:type", Number)
], PvdExpandCollapse.prototype, "pvdTitleHeadingLevel", void 0);

export { DEFAULT_DISABLE_ANIMATION, DEFAULT_EXPAND, DEFAULT_EXPAND_ON_PRINT, DEFAULT_SIZE, EXPAND_COLLAPSE_ANIMATION_DURATION, ExpandCollapseState, PvdExpandCollapse };
//# sourceMappingURL=pvd-expand-collapse.js.map
